import React, { useContext, useState } from 'react'
import { Grid, useMediaQuery } from '@mui/material';
import { useApiKeyStore } from '../../../store/apiKeyStore';
import GlobalButton from '../../../components/utility/globalButton/button';
import AddIcon from '@mui/icons-material/Add';
import { GlobalTable } from '../../../components/utility/globalTable';
import GlobalPopUp from '../../../components/utility/globalPopUp';
import CreateApiKeyPopup from './createApiKeyPopup';
import { apiKeyData } from '../../../models/axiosModel';
import GlobalDeletePopup from '../../../components/utility/globalDeletePopup/globalDeletePopup';
import { UserContext, UserContextPayload } from '../../../context/userContext';
import agent from '../../../services/api';
import SaveApiKeyPopup from './saveApiKeyPopup';
import ApikeyInfo from './apikeyInfo';
import { useGenericStore } from '../../../store/genericStore';
import { useShallow } from 'zustand/react/shallow';

interface ApiKeysProps {
    getApiKey: () => Promise<void>,
}

const ApiKeys: React.FC<ApiKeysProps> = (props) => {

    const { getApiKey } = props;
    const { setFlagLoading, handleError } = useContext(UserContext) as UserContextPayload;

    const apikeyData = useApiKeyStore((store) => store.apikeyData);
    const flagCreateApiKeyPopup = useApiKeyStore((store) => store.flagCreateApiKeyPopup);
    const setFlagCreateApiKeyPopup = useApiKeyStore((store) => store.setFlagCreateApiKeyPopup);
    const flagSaveApiKeyPopup = useApiKeyStore((store) => store.flagSaveApiKeyPopup);
    const setFlagSaveApiKeyPopup = useApiKeyStore((store) => store.setFlagSaveApiKeyPopup);

    const { showSuccess } = useGenericStore(useShallow((state) => ({
        showSuccess: state.showSuccess
    })))

    const [showDeleteApiKeyConfirmation, setShowDeleteApiKeyConfirmation] = useState<boolean>(false);
    const [selectedApiKey, setSelectedApiKey] = useState<apiKeyData | null>(null);

    const breakPoints = {
        smallScreen: useMediaQuery<boolean>('(max-width : 600px) and (min-width : 0px)'),
        flagSmallScreen: useMediaQuery<boolean>('(min-width: 0px) and (max-width: 1000px)')
    }

    const tableHeaderList = [
        {
            name: "#",
            alignment: "left",
            flagIcon: false,
            valueKey: "index"
        },
        {
            name: "API Key Name",
            alignment: "left",
            flagIcon: false,
            valueKey: "api_key_name"
        },
        {
            name: "API Key",
            alignment: "left",
            flagIcon: false,
            valueKey: "api_key_value"
        },
        {
            name: "Created At",
            alignment: "left",
            flagIcon: false,
            valueKey: "created_at_timestamp"
        },
        {
            name: "",
            alignment: "right",
            flagIcon: true,
            valueKey: "",
            iconKey: "deleteIcon"
        }
    ]

    const checkDisableCreateApiKeyBtn = () => {
        if (apikeyData && apikeyData.length >= 3) {
            return true;
        }
        return false;
    }

    const onClickOfCreateApiKey = () => {
        setFlagCreateApiKeyPopup(true);
    }

    const onClickOfDeleteApiKey = (e: React.MouseEvent<HTMLImageElement, MouseEvent>, apiKeyData: apiKeyData) => {
        setSelectedApiKey(apiKeyData);
        setShowDeleteApiKeyConfirmation(true);
    }

    const deleteApiKey = async () => {
        if (selectedApiKey) {
            setFlagLoading((prev) => ({ ...prev, flagRoundLoader: true }));
            const res = await agent.deleteApiKey.delete(selectedApiKey.user_api_key_id);
            if (!(res.status)) {
                handleError(res);
            } else {
                showSuccess("API Key deleted successfully")
                getApiKey();
            }
            setFlagLoading((prev) => ({ ...prev, flagRoundLoader: false }));
        }
    }

    return (
        <>
            <Grid container gap={3}>
                <Grid item xs={12}>
                    <ApikeyInfo />
                </Grid>

                <Grid item xs={12}>
                    <Grid container gap={2}>
                        <Grid item xs={12}>
                            <Grid container justifyContent={breakPoints.smallScreen ? "start" : "end"}>
                                <Grid item>
                                    <GlobalButton
                                        className='primaryButtonStyle'
                                        buttonText="Create API Key"
                                        sx={{ width: "180px" }}
                                        startIcon={<AddIcon />}
                                        disabled={checkDisableCreateApiKeyBtn()}
                                        onClick={() => onClickOfCreateApiKey()}
                                    />
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item xs={12}>
                            <GlobalTable
                                headerList={tableHeaderList}
                                rowData={apikeyData ?? []}
                                tableHeight={"72vh"}
                                tableContainerHeight={"auto"}
                                tableContainerMinHeight={"auto"}
                                onRowClick={(e: any) => { }}
                                flagPagination
                                deleteRowData={onClickOfDeleteApiKey}
                            />
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {flagCreateApiKeyPopup &&
                <GlobalPopUp
                    open={flagCreateApiKeyPopup}
                    onClose={() => { setFlagCreateApiKeyPopup(false) }}
                    handleClose={() => { setFlagCreateApiKeyPopup(false) }}
                    flagShowCloseIcon={true}
                    paperMinWidth={breakPoints.flagSmallScreen ? "300px" : "400px"}
                    paperMaxwidth={breakPoints.flagSmallScreen ? "400px" : "500px"}
                    borderRadius='10px'
                    titlePadding='0px'
                    overflow="hidden"
                >
                    <CreateApiKeyPopup
                        getApiKey={getApiKey}
                    />
                </GlobalPopUp>
            }

            {flagSaveApiKeyPopup &&
                <GlobalPopUp
                    open={flagSaveApiKeyPopup}
                    onClose={() => { setFlagSaveApiKeyPopup(false) }}
                    handleClose={() => { setFlagSaveApiKeyPopup(false) }}
                    // flagShowCloseIcon={true}
                    paperMinWidth={breakPoints.flagSmallScreen ? "300px" : "400px"}
                    paperMaxwidth={breakPoints.flagSmallScreen ? "400px" : "500px"}
                    borderRadius='10px'
                    titlePadding='0px'
                    overflow="hidden"
                    flagBackdrop
                >
                    <SaveApiKeyPopup />
                </GlobalPopUp>
            }

            {showDeleteApiKeyConfirmation &&
                <GlobalDeletePopup
                    deleteDescription={`Are you sure you want to delete ${selectedApiKey?.api_key_name} key`}
                    open={showDeleteApiKeyConfirmation}
                    onClose={() => { setShowDeleteApiKeyConfirmation(false) }}
                    onDelete={() => {
                        deleteApiKey()
                        setShowDeleteApiKeyConfirmation(false)
                    }}
                />
            }
        </>
    )
}

export default ApiKeys
